
import React, {useEffect, useState} from "react";
import {Navigation} from "@/components/Navigation";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {
    Wallet,
    Loader2,
    User,
    BadgeCheck,
    Mail,
    Lock,
    Globe,
    BarChart3,
    Tag,
    AtSign,
} from "lucide-react";
import { SiX } from "react-icons/si";
import {toast} from "sonner";
import {Footer} from "@/components/Footer";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {Badge} from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import countries from "../utils/countries";
import {useQuery} from "@tanstack/react-query";
import {Session} from "@supabase/supabase-js";
import {useForm} from "react-hook-form";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Skeleton} from "@/components/ui/skeleton";
import supabase from "@/utils/supabaseClient.ts";
import ReferralCode from "@/components/ReferralCode.tsx";
import {useNavigate} from "react-router-dom";
import {Category} from "@/types/Category.ts";
import {SEO} from "@/components/SEO.tsx";
import {useAuth} from "@/contexts/AuthContext.tsx";

const AccountSkeleton = () => (
    <div className="space-y-4 px-4 py-2">
        <Skeleton className="h-10 w-1/2 bg-gray-200"/>
        <Skeleton className="h-10 w-1/2 bg-gray-200"/>
        <Skeleton className="h-10 w-full bg-gray-200"/>
        <Skeleton className="h-10 w-full bg-gray-200"/>
        <Skeleton className="h-10 w-24"/>
    </div>
);

interface AccountFormValues {
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    nationality?: string;
    interests?: string[]
}

// interface Category {
//     id: "3acbc873-7888-4a25-b09c-a76b320c8f3d",
//     name: "Politics",
//     slug: "politics",
// }

const Account = () => {
    const form = useForm<AccountFormValues>({
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            username: "",
            nationality: "",
        },
    });
    const [copied, setCopied] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [isUpdateButtonLoading, setIsUpdateButtonLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [walletAddress, setWalletAddress] = useState("");
    const [balance, setBalance] = useState("0");
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 3;
    const [referralCode, setReferralCode] = useState("");
    /*const [userSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });*/
    const {user, session, loading} = useAuth();
    const navigate = useNavigate();
    const [isXLoading, setIsXLoading] = useState(false);
    const connectX_old = async () => {
        setIsXLoading(true)
        const X_REDIRECT_URI = "http://localhost:8081/account";

        const authUrl = `https://x.com/i/oauth2/authorize?response_type=code&client_id=${import.meta.env.VITE_X_CLIENT_ID}&redirect_uri=${X_REDIRECT_URI}&scope=tweet.read%20tweet.write%20users.read&state=randomString&code_challenge=challenge&code_challenge_method=plain`;
        window.location.href = authUrl;

    }

    // Örnek: Twitter Hesabını Bağla butonuna tıklandığında çalışacak metot
    const connectX = async () => {
        // Gerekli değerleri tanımlayın (bu değerleri .env dosyanızdan alabilirsiniz)
        const clientId = import.meta.env.VITE_TWITTER_CLIENT_ID;
        const redirectUri = "http://localhost:8081/x-callback";

        // state: CSRF koruması için rastgele oluşturulabilir
        const state = Math.random().toString(36).substring(2);

        // PKCE için code_verifier oluşturup, bunun code_challenge’ını hesaplayın.
        // Aşağıdaki örnekte basit bir placeholder gösterilmiştir.
        const codeVerifier = generateCodeVerifier();        // PKCE için S256 algoritmasıyla code_challenge hesaplaması yapmanız gerekecektir.
        localStorage.setItem("x-verify",codeVerifier);
        const codeChallenge = await generateCodeChallenge(codeVerifier); // Kendi fonksiyonunuzu tanımlayın

        // İstenen kapsamları belirleyin (kendi ihtiyaçlarınıza göre düzenleyin)
        const scope = encodeURIComponent("tweet.read users.read follows.read like.read offline.access");

        // Güncel yetkilendirme URL'si; not: artık "x.com" domain’i kullanılıyor.
        const authUrl = `https://x.com/i/oauth2/authorize?response_type=code` +
            `&client_id=${clientId}` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&scope=${scope}` +
            `&state=${state}` +
            `&code_challenge=${codeChallenge}` +
            `&code_challenge_method=S256`;

        // Kullanıcıyı yetkilendirme sayfasına yönlendirin
        window.location.href = authUrl;
    };

    function generateCodeVerifier(length = 128) {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        let verifier = '';
        const randomValues = new Uint8Array(length);
        window.crypto.getRandomValues(randomValues);
        for (let i = 0; i < length; i++) {
            verifier += charset[randomValues[i] % charset.length];
        }
        return verifier;
    }


    async function generateCodeChallenge(codeVerifier) {
        // TextEncoder ile codeVerifier'ı Uint8Array'e dönüştürün
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);

        // SHA-256 hash hesaplaması yapın
        const digest = await window.crypto.subtle.digest('SHA-256', data);

        // Uint8Array'i normal base64 string'e çevirin
        const byteArray = new Uint8Array(digest);
        const base64String = btoa(String.fromCharCode(...byteArray));

        // Base64 string'i base64url formatına dönüştürün:
        // + -> -, / -> _ ve sondaki = karakterlerini kaldırın
        const base64Url = base64String
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');

        return base64Url;
    }


    const connectWallet = async () => {
        setIsConnecting(true);
        // Simulating wallet connection
        setTimeout(() => {
            const mockAddress = "0x742d35Cc6634C0532925a3b844Bc454e4438f44e";
            setWalletAddress(mockAddress);
            setBalance("1000");
            setIsConnected(true);
            setIsConnecting(false);
            toast.success("Wallet connected successfully!");
        }, 1500);
    };

    const disconnectWallet = () => {
        setIsConnected(false);
        setWalletAddress("");
        setBalance("0");
        toast.success("Wallet disconnected successfully!");
    };

    /*const categories = [
        "Sports",
        "Technology",
        "Politics",
        "Entertainment",
        "Business",
        "Science",
        "Health",
    ];*/

    const toggleCategory = async (category: Category) => {
        // console.log("toggle category:", category)
        // console.log(selectedCategories)
        setSelectedCategories((prev) =>
            prev.includes(category)
                ? prev.filter((c) => c !== category)
                : [...prev, category]
        );
        const updateInterests: AccountFormValues = {
            interests: selectedCategories.includes(category) ? (selectedCategories.filter((c) => c !== category)).map(x => x.id) : [...(selectedCategories.map(x => x.id)), category.id]
        }
        await callApi(updateInterests);

    };

    const pollData = [
        {
            poll: "World Cup 2024 Winner",
            vote: "Brazil",
            result: "Success",
            points: 100,
        },
        {
            poll: "Best Movie 2024",
            vote: "Oppenheimer",
            result: "Success",
            points: 80,
        },
        {
            poll: "Tech Trends 2024",
            vote: "AI",
            result: "Fail",
            points: 0,
        },
        {
            poll: "Oscar Winners 2024",
            vote: "Poor Things",
            result: "Success",
            points: 120,
        },
        {
            poll: "Eurovision 2024",
            vote: "Sweden",
            result: "Fail",
            points: 0,
        },
    ];

    const {data: data, isLoading, refetch} = useQuery({
        queryKey: ['users', session],
        queryFn: async () => {
            // const session = await supabase.auth.getSession();
            if (session) {
                const url = `${import.meta.env.VITE_API_URL}/users`;
                const headers = {
                    'Authorization': `Bearer ${session.access_token}`
                };
                const response = await fetch(url, {
                    method: "GET",
                    headers,
                    redirect: "follow"
                });
                const responseData = await response.json();
                // console.log("responseData:", responseData)
                // form.setValue("firstName",responseData?.data?.firstName);
                // console.log("responseData:", responseData?.data?.firstName)

                return responseData;
            }
            return;

        },
        enabled: true
    });
    const [totalPages, setTotalPages] = useState(1)
    // const totalPages = Math.ceil(pollData.length / itemsPerPage);
    // const paginatedData = pollData.slice(
    //     (currentPage - 1) * itemsPerPage,
    //     currentPage * itemsPerPage
    // );
    const {data: summary, isLoading: isSummaryLoading} = useQuery({
        queryKey: ['summary', session, currentPage, itemsPerPage],
        queryFn: async () => {
            // const session = await supabase.auth.getSession();
            if (session) {
                const url = `${import.meta.env.VITE_API_URL}/users/summary?page=${currentPage}&pageSize=${itemsPerPage}`;
                const headers = {
                    'Authorization': `Bearer ${session.access_token}`
                };
                const response = await fetch(url, {
                    method: "GET",
                    headers,
                });
                const responseData = await response.json();
                // console.log("summary:", responseData)
                // console.log("totalRecords:", responseData?.userVote?.totalRecords)
                // setTotalPages(Math.ceil(responseData?.userVote?.totalRecords / itemsPerPage))
                return responseData;
            }
            return;

        },
        enabled: true
    });

    const {data: categories, isLoading: isCategoriesLoading} = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/categories`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const responseData = await response.json();
            // console.log("category response:", responseData)
            return responseData;
        }
    });
    useEffect(() => {
        if(copied){
            toast.success("Copied!")
        }
    }, [copied]);
    useEffect(() => {
        if (data && data.data) {
            form.setValue("firstName", data.data.firstName || "");
            form.setValue("lastName", data.data.lastName || "");
            form.setValue("email", data.data.email || "");
            form.setValue("nationality", data.data.nationality || "");
            form.setValue("username",data.data.username || "");
            setReferralCode(data.data.userCode);
            // Add null check for interests
            const filteredCategories = categories?.filter((c) =>
                data.data.interests ? data.data.interests.includes(c.id) : false
            );
            setSelectedCategories(filteredCategories || []);
        }
        // console.log("form:", form.getValues());
    }, [data, form]);

    const onSubmit = async (accountFormValues: AccountFormValues) => {
        // console.log("submitData:", accountFormValues)
        if(accountFormValues.username == null || accountFormValues.username.trim() == ""){
            return toast.error("Username can't be null!");
        }
        await callApi(accountFormValues);
        // return;

    };

    const callApi = async (accountFormValues: AccountFormValues) => {
        // console.log("call Api params:", accountFormValues)
        try {
            const url = `${import.meta.env.VITE_API_URL}/users`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            };
            const body = JSON.stringify(accountFormValues);

            const response = await fetch(url, {
                method: 'PUT',
                headers,
                body,
            });

            if (!response.ok) {
                return toast.error('An error occurred while saving personal info!');
            }
            refetch();
            return toast.success('Personal information saved!');
        } catch (error) {
            // console.error('Error:', error);
        }
    }

    const updatePassword = async () => {
        setIsUpdateButtonLoading(true);
        if (!newPassword) {
            return toast.error('New password can not be empty!');
        }

        if(!currentPassword){
            return toast.error('Please enter your current password!');
        }

        try {
            const session: Session = JSON.parse(localStorage.getItem("session"));
            if(session && session?.user){
                const { error: signInError } = await supabase.auth.signInWithPassword({
                    email: session.user.email,
                    password: currentPassword,
                });
                if(signInError){
                    setIsUpdateButtonLoading(false);
                    return toast.error("Please check your current password!");
                }
            }

            const {error} = await supabase.auth.updateUser({password: newPassword});

            // Hata kontrolü
            if (error) {
                setIsUpdateButtonLoading(false);
                return toast.error(error.message);
            }
            // Giriş başarılıysa
            toast.success('Password updated successfully!');


        } catch (err) {
            // console.error('Update error:', err);
            toast.error('An error occurred.');
        }

        setIsUpdateButtonLoading(false);
        setNewPassword("");
        setCurrentPassword("")
    }

    return (
        <div className="min-h-screen bg-background font-sans flex flex-col dark:bg-background">
            <SEO
                title="Account"
                description="Manage your profile, track your poll activity, and customize your experience"
                type="website"
                name="blankpoll"
                imageUrl="/blankpoll-banner.jpg"
            />
            <Navigation/>
            <div className="flex-1">
                <div className="max-w-7xl mx-auto pt-24 px-2 sm:px-4 md:px-6 lg:px-8">
                    <div className="flex items-center gap-3 mb-8">
                        <h1 className="text-3xl font-bold dark:text-white">Account</h1>
                        {/*{(data?.data?.firstName && data?.data?.lastName && data?.data?.nationality && data?.data?.username) && (
                            <Badge variant="outline" className="gap-1 dark:border-gray-700">
                                <BadgeCheck className="w-3 h-3 text-primary"/>
                                <span className="dark:text-gray-300">Verified User</span>
                            </Badge>
                        )}*/}

                    </div>

                    <div className="grid gap-4 lg:gap-6 lg:grid-cols-3">
                        {/* Profile Information */}
                        <div className="lg:col-span-2">
                            <Card className="overflow-hidden dark:bg-gray-800/50 dark:border-gray-700">
                                <Form {...form}>
                                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2 dark:text-white">
                                                <User className="h-6 w-6"/>
                                                Profile Information
                                            </CardTitle>
                                        </CardHeader>
                                        {isLoading ? (
                                            <CardContent>
                                                <AccountSkeleton />
                                            </CardContent>
                                        ) : (
                                            <CardContent className="space-y-4">
                                                <div className="flex flex-col items-start">
                                                    <label className="mb-1 text-sm font-medium">Referral Code</label>
                                                    <ReferralCode code={referralCode} copied={copied} setCopied={setCopied}/>
                                                </div>
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div>
                                                        <FormField
                                                            control={form.control}
                                                            name="firstName"
                                                            render={({field}) => (
                                                                <FormItem>
                                                                    <FormLabel
                                                                        className="text-sm font-medium mb-2 block dark:text-gray-300">First
                                                                        Name</FormLabel>
                                                                    <FormControl>
                                                                        <Input placeholder="John" {...field}
                                                                               className="dark:bg-gray-700 dark:text-white dark:border-gray-600"/>
                                                                    </FormControl>
                                                                    <FormMessage/>
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormField
                                                            control={form.control}
                                                            name="lastName"
                                                            render={({field}) => (
                                                                <FormItem>
                                                                    <FormLabel
                                                                        className="text-sm font-medium mb-2 block dark:text-gray-300">Last
                                                                        Name</FormLabel>
                                                                    <FormControl>
                                                                        <Input placeholder="Doe" {...field}
                                                                               className="dark:bg-gray-700 dark:text-white dark:border-gray-600"/>
                                                                    </FormControl>
                                                                    <FormMessage/>
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <FormField
                                                        control={form.control}
                                                        name="email"
                                                        render={({field}) => (
                                                            <FormItem>
                                                                <FormLabel
                                                                    className="text-sm font-medium mb-2 block dark:text-gray-300">Email
                                                                    Address</FormLabel>
                                                                <FormControl>
                                                                    <Input disabled type="email"
                                                                           placeholder="john.doe@example.com" {...field}
                                                                           className="dark:bg-gray-700 dark:text-white dark:border-gray-600"/>
                                                                </FormControl>
                                                                <FormMessage/>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                <div>
                                                    <FormField
                                                        control={form.control}
                                                        name="username"
                                                        render={({field}) => (
                                                            <FormItem>
                                                                <FormLabel
                                                                    className="text-sm font-medium mb-2 block dark:text-gray-300">Username</FormLabel>
                                                                <FormControl>
                                                                    <Input type="text"
                                                                           placeholder="johndoe" {...field}
                                                                           className="dark:bg-gray-700 dark:text-white dark:border-gray-600"/>
                                                                </FormControl>
                                                                <FormMessage/>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                <div>
                                                    <FormField
                                                        control={form.control}
                                                        name="nationality"
                                                        render={({field}) => (
                                                            <FormItem>
                                                                <FormLabel
                                                                    className="text-sm font-medium mb-2 block dark:text-gray-300">Nationality </FormLabel>
                                                                <FormControl>
                                                                    <Select onValueChange={field.onChange}
                                                                            value={field.value}
                                                                            disabled={data?.data?.nationality}
                                                                    >
                                                                        <SelectTrigger
                                                                            className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600">
                                                                            <SelectValue
                                                                                className="bg-white dark:bg-gray-700"
                                                                                placeholder="Select your country"/>
                                                                        </SelectTrigger>
                                                                        <SelectContent className="dark:bg-gray-800">
                                                                            {countries.map((country) => (
                                                                                <SelectItem
                                                                                    className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700"
                                                                                    key={country.code}
                                                                                    value={country.code}
                                                                                >
                                                                                    {country.name}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                </FormControl>
                                                                <FormMessage/>
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                                <Button
                                                    className="bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90">Save
                                                    Changes</Button>
                                            </CardContent>
                                        )}
                                    </form>
                                </Form>
                            </Card>
                        </div>

                        {/* Right Column - Wallet Connection and Interests */}
                        <div className="space-y-4 lg:space-y-6">
                            {/* Wallet Connection */}
                            {/*<Card className="overflow-hidden dark:bg-gray-800/50 dark:border-gray-700">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 dark:text-white">
                                        <SiX className="h-6 w-6"/>

                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                        <div className="space-y-4">
                                            <p className="text-gray-600 dark:text-gray-300">
                                                Connect your X account.
                                            </p>
                                            {data?.x ? (
                                                <div>
                                                    {data.x}
                                                </div>
                                                ) : (
                                                <Button
                                                    onClick={connectX}
                                                    disabled={isXLoading}
                                                    className="w-full bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                                                >
                                                    {isXLoading ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                                        </>
                                                    ) : (
                                                        "Connect"
                                                    )}
                                                </Button>
                                            )}

                                        </div>
                                </CardContent>
                            </Card>*/}
                            <Card className="overflow-hidden dark:bg-gray-800/50 dark:border-gray-700">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 dark:text-white">
                                        <Wallet className="h-6 w-6"/>
                                        Wallet Connection
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {!isConnected ? (
                                        <div className="space-y-4">
                                            <p className="text-gray-600 dark:text-gray-300">
                                                Connect your wallet to participate in polls and earn rewards.
                                            </p>
                                            <Button
                                                onClick={connectWallet}
                                                disabled={true}
                                                className="w-full bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                                            >
                                                {isConnecting ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                                                        Connecting...
                                                    </>
                                                ) : (
                                                    "Coming Soon"
                                                )}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="grid gap-2">
                                                <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                                                    <span className="text-gray-600 dark:text-gray-300">Wallet Address</span>
                                                    <span className="font-mono text-sm break-all dark:text-gray-300">{walletAddress}</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b dark:border-gray-700">
                                                    <span className="text-gray-600 dark:text-gray-300">Balance</span>
                                                    <span className="font-bold dark:text-white">{balance} USDT</span>
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                onClick={disconnectWallet}
                                                className="w-full hover:bg-[#96A58D] hover:text-white dark:border-gray-600 dark:text-gray-200 dark:hover:bg-[#2C3930]"
                                            >
                                                Disconnect Wallet
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Interests */}
                            <Card className="overflow-hidden dark:bg-gray-800/50 dark:border-gray-700">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 dark:text-white">
                                        <Tag className="h-6 w-6"/>
                                        Interests
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                                        Select categories you're interested in to get personalized poll recommendations.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {categories && categories?.map((category: Category) => (
                                            <Badge
                                                key={category.id}
                                                variant={selectedCategories.includes(category) ? "default" : "outline"}
                                                className={`cursor-pointer hover:bg-primary/90 dark:border-gray-600 ${
                                                    selectedCategories.includes(category)
                                                        ? "dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                                                        : "dark:text-gray-200 dark:hover:bg-[#2C3930]"
                                                }`}
                                                onClick={() => toggleCategory(category)}
                                            >
                                                {category.name}
                                            </Badge>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Change Password */}
                        <Card className="lg:col-span-2 overflow-hidden dark:bg-gray-800/50 dark:border-gray-700">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 dark:text-white">
                                    <Lock className="h-6 w-6"/>
                                    Change Password
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <Input
                                        type="password"
                                        placeholder="Current password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                    />
                                    <Input
                                        type="password"
                                        placeholder="New password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                    />
                                </div>
                                <Button
                                    className="mt-4 bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                                    onClick={updatePassword}
                                >
                                    {isUpdateButtonLoading ? "Updating..." : "Update Password"}
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Poll Activity */}
                        <div className="lg:col-span-2">
                            <Card className="overflow-hidden dark:bg-gray-800/50 dark:border-gray-700">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 dark:text-white">
                                        <BarChart3 className="h-6 w-6"/>
                                        Poll Activity
                                    </CardTitle>
                                </CardHeader>
                                {isSummaryLoading ? (
                                    <>
                                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                                            <Skeleton className="bg-gray-200 dark:bg-gray-700 p-4 rounded-lg"/>
                                            <Skeleton className="bg-gray-200 dark:bg-gray-700 p-4 rounded-lg"/>
                                            <Skeleton className="bg-gray-200 dark:bg-gray-700 p-4 rounded-lg"/>
                                        </div>
                                        <div className="w-full h-20 overflow-auto">
                                            <Skeleton className="bg-gray-200 dark:bg-gray-700"/>
                                            <Skeleton className="bg-gray-200 dark:bg-gray-700"/>
                                        </div>
                                    </>
                                ) : (summary?.userVote &&
                                    <CardContent>
                                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                                            <div className="bg-accent dark:bg-gray-700/50 p-4 rounded-lg">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Total Polls</div>
                                                <div className="text-2xl font-bold dark:text-white">{summary?.userVote?.totalRecords}</div>
                                            </div>
                                            <div className="bg-accent dark:bg-gray-700/50 p-4 rounded-lg">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Success Rate</div>
                                                <div className="text-2xl font-bold text-green-600">{summary?.successRate}</div>
                                            </div>
                                            <div className="bg-accent dark:bg-gray-700/50 p-4 rounded-lg">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">Total Points</div>
                                                <div className="text-2xl font-bold dark:text-white">{summary?.totalPoints}</div>
                                            </div>
                                        </div>

                                        <div className="w-full overflow-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="dark:border-gray-700">
                                                        <TableHead className="dark:text-gray-300">Poll</TableHead>
                                                        <TableHead className="dark:text-gray-300">Your Vote</TableHead>
                                                        <TableHead className="dark:text-gray-300">Result</TableHead>
                                                        <TableHead className="dark:text-gray-300">Points</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {summary?.userVote?.userVotes?.map((poll, index) => (
                                                        <TableRow key={index} className="dark:border-gray-700">
                                                            <TableCell className="dark:text-gray-300">{poll.title}</TableCell>
                                                            <TableCell className="dark:text-gray-300">
                                                                {poll.options.find((o) => o.id == poll.users_vote).title}
                                                            </TableCell>
                                                            <TableCell className={poll.correct_option_id ? (poll.correct_option_id === poll.users_vote) ? "text-green-600" : "text-red-600" : "text-primary dark:text-gray-300"}>
                                                                {poll.correct_option_id ? (poll.correct_option_id === poll.users_vote) ? "Success" : "Failed" : "Pending"}
                                                            </TableCell>
                                                            <TableCell className="dark:text-gray-300">{poll.prize}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        <div className="flex justify-center gap-2 mt-4">
                                            <Button
                                                variant="outline"
                                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-[#2C3930]"
                                            >
                                                Previous
                                            </Button>
                                            <span className="py-2 px-4 dark:text-gray-300">
                                                {/*Page {currentPage} of {totalPages}*/}
                                                Page {currentPage} of {summary?.userVote?.pages}
                                            </span>
                                            <Button
                                                variant="outline"
                                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, summary?.userVote?.pages))}
                                                // disabled={currentPage === totalPages}
                                                disabled={currentPage === summary?.userVote?.pages}
                                                className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-[#2C3930]"
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </CardContent>
                                )}
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
    );
};

export default Account;
